import type React from 'react';
import { useCallback, useRef, useState } from 'react';

export type AnchorMenu<T extends HTMLElement = HTMLElement> = {
  anchorEl: T | null;
  isOpen: boolean;
  open: (event: React.MouseEvent<T>) => void;
  close: () => void;
  toggle: (event: React.MouseEvent<T>) => void;
  /**
   * Close the menu, then run `action` once the exit transition has finished.
   *
   * Replaces the `setAnchorEl(null); setTimeout(fn, 0);` idiom that appeared in
   * five places. Those were racing MUI's transition — the timeout happens to win
   * on a fast frame and loses on a slow one, which is why focus occasionally
   * ended up back on the trigger. This waits for `onExited` instead, so it is
   * deterministic rather than lucky.
   */
  closeThen: (action: () => void) => void;
  /** Spread onto `DropdownMenu` or MUI `Menu`; carries the `onExited` hook. */
  menuProps: {
    anchorEl: T | null;
    open: boolean;
    onClose: () => void;
    TransitionProps: { onExited: () => void };
  };
};

type UseAnchorMenuOptions = {
  /** Stop the opening click reaching a draggable or clickable ancestor. */
  stopPropagation?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
};

/**
 * Owns the `anchorEl` lifecycle for an anchored MUI `Menu` or `Popover`.
 *
 * Seven components each hand-rolled this state with inconsistent types
 * (`HTMLElement | null` vs `null | HTMLElement`) and inconsistent memoization.
 */
export function useAnchorMenu<T extends HTMLElement = HTMLElement>({
  stopPropagation = true,
  onOpen,
  onClose,
}: UseAnchorMenuOptions = {}): AnchorMenu<T> {
  const [anchorEl, setAnchorEl] = useState<T | null>(null);
  const pendingAction = useRef<(() => void) | null>(null);

  const open = useCallback(
    (event: React.MouseEvent<T>) => {
      if (stopPropagation) {
        event.stopPropagation();
      }
      setAnchorEl(event.currentTarget);
      onOpen?.();
    },
    [stopPropagation, onOpen]
  );

  const close = useCallback(() => {
    setAnchorEl(null);
    onClose?.();
  }, [onClose]);

  const toggle = useCallback(
    (event: React.MouseEvent<T>) => {
      if (stopPropagation) {
        event.stopPropagation();
      }
      setAnchorEl((previous) => (previous ? null : event.currentTarget));
    },
    [stopPropagation]
  );

  const closeThen = useCallback(
    (action: () => void) => {
      pendingAction.current = action;
      close();
    },
    [close]
  );

  const handleExited = useCallback(() => {
    const action = pendingAction.current;
    pendingAction.current = null;
    action?.();
  }, []);

  return {
    anchorEl,
    isOpen: Boolean(anchorEl),
    open,
    close,
    toggle,
    closeThen,
    menuProps: {
      anchorEl,
      open: Boolean(anchorEl),
      onClose: close,
      TransitionProps: { onExited: handleExited },
    },
  };
}

export default useAnchorMenu;
